import React, { useEffect, useMemo, useState } from 'react';
import {
  LightBulbIcon,
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';
import { useMetaSugestao } from '../../hooks/useMetaSugestao';
import { money } from '../../utils/format';
import Sparkline from '../ui/Sparkline';
import { btnPrimario } from '../ui/iris';
import type { DimensaoSugestao } from '../../types/metaSugestao';

interface Props {
  empresas: string[];
  dimensao?: DimensaoSugestao;
  codigoDimensao?: string;
  /** Callback ao clicar "Aplicar" — pai atualiza valorMeta no form. */
  onAplicar: (valor: number) => void;
}

function nomeMesCurto(anoMes: string): string {
  const [a, m] = anoMes.split('-');
  const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const idx = Number.parseInt(m, 10) - 1;
  return `${nomes[idx] ?? m}/${a.slice(2)}`;
}

/** Formato compacto pra R$ — "R$ 32,5k" / "R$ 1,2M". */
function moneyCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1_000)     return `R$ ${(value / 1_000).toFixed(1).replace('.', ',')}k`;
  return money(value);
}

/**
 * Card de inteligência do modal de meta. Junta sugestão (compras 3M) + panorama
 * de vendas (3M atual vs 3M anterior + crescimento) + sparklines 6M, tudo no
 * mesmo espaço — operador vê em ~3 segundos se vale a meta proposta.
 *
 * Reusado em ModalMetaCompra e ModalMetaCompraDimensao.
 */
const SugestaoMetaInteligente: React.FC<Props> = ({ empresas, dimensao, codigoDimensao, onAplicar }) => {
  const [ajustePct, setAjustePct] = useState(0);

  const filtros = useMemo(() => ({
    empresas,
    dimensao,
    codigo: codigoDimensao,
  }), [empresas, dimensao, codigoDimensao]);

  const { data, isFetching, error } = useMetaSugestao(filtros);

  // Reset do ajuste ao trocar contexto — evita +30% de um fornecedor ficar
  // stuck ao trocar pra outro.
  useEffect(() => {
    setAjustePct(0);
  }, [empresas.join(','), dimensao, codigoDimensao]);

  const incompleto = empresas.length === 0 || (dimensao && !codigoDimensao);
  if (incompleto) return null;

  if (isFetching && !data) {
    return (
      <div className="rounded-iris border border-line bg-accent-soft p-3">
        <div className="flex items-center gap-2 text-xs text-accent-text">
          <LightBulbIcon className="h-4 w-4 animate-pulse" />
          Calculando contexto baseado nos últimos 6 meses…
        </div>
      </div>
    );
  }

  if (error || !data) return null;

  if (data.semHistorico) {
    return (
      <div className="rounded-iris border border-line bg-surface-2 p-3 text-xs text-tx-2">
        <div className="flex items-start gap-2">
          <LightBulbIcon className="mt-0.5 h-4 w-4 shrink-0 text-tx-3" />
          <div>
            <p className="font-medium text-tx">Sem histórico em {data.periodoLabel}</p>
            <p className="mt-0.5">Sem compra registrada nos 3 últimos meses fechados pra esta combinação. Defina o valor manualmente.</p>
          </div>
        </div>
      </div>
    );
  }

  const metaFinal = Math.max(0, Math.round((data.mediaMensal * (1 + ajustePct / 100)) * 100) / 100);

  // Cor do crescimento
  const cresc = data.vendas?.crescimentoPct ?? 0;
  const crescPositivo = cresc > 1;
  const crescNegativo = cresc < -1;
  const crescIcon = crescPositivo ? ArrowTrendingUpIcon : crescNegativo ? ArrowTrendingDownIcon : MinusIcon;
  const crescColor = crescPositivo
    ? 'text-ok'
    : crescNegativo
    ? 'text-danger'
    : 'text-tx-2';
  const crescBg = crescPositivo
    ? 'bg-ok-soft'
    : crescNegativo
    ? 'bg-danger-soft'
    : 'bg-surface-2';

  const comprasValores = data.compras6M.map((m) => m.valor);
  const vendasValores = data.vendas?.vendas6M.map((m) => m.valor) ?? [];
  const CrescIcon = crescIcon;

  return (
    <div className="rounded-iris border border-line bg-accent-soft p-3">
      {/* Cabeçalho compacto */}
      <div className="mb-2 flex items-center gap-2">
        <LightBulbIcon className="h-4 w-4 shrink-0 text-accent-text" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-accent-text">
          Inteligência do período
        </span>
        <span className="text-[10px] text-tx-3">· últimos 6 meses fechados</span>
      </div>

      {/* Grid 2 colunas: Compras (sempre) + Vendas (se tiver) */}
      <div className={`grid gap-2 ${data.vendas && !data.vendas.semHistorico ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* Painel Compras */}
        <div className="rounded-iris border border-line bg-surface p-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-tx-3">Compra · média/mês</div>
              <div className="truncate text-base font-bold text-tx">{money(data.mediaMensal)}</div>
              <div className="text-[10px] text-tx-3">{data.periodoLabel}</div>
            </div>
            <Sparkline
              values={comprasValores}
              width={80}
              height={28}
              color="var(--accent)"
              ariaLabel="Evolução de compras nos últimos 6 meses"
            />
          </div>
          <div className="mt-1.5 flex gap-1 text-[9px] text-tx-3">
            {data.compras6M.map((m) => (
              <div key={m.anoMes} className="flex-1 text-center" title={`${nomeMesCurto(m.anoMes)}: ${money(m.valor)}`}>
                {nomeMesCurto(m.anoMes)}
              </div>
            ))}
          </div>
        </div>

        {/* Painel Vendas (oculto pra fornecedor ou sem histórico) */}
        {data.vendas && !data.vendas.semHistorico && (
          <div className="rounded-iris border border-ok-border bg-surface p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-tx-3">Vendas · últimos 3M</div>
                <div className="truncate text-base font-bold text-tx">{moneyCompact(data.vendas.vendas3M)}</div>
                <div className={`mt-0.5 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium ${crescBg} ${crescColor}`}>
                  <CrescIcon className="h-2.5 w-2.5" />
                  {cresc > 0 ? '+' : ''}{cresc.toFixed(1)}% vs 3M ant.
                </div>
              </div>
              <Sparkline
                values={vendasValores}
                width={80}
                height={28}
                color="var(--ok)"
                ariaLabel="Evolução de vendas nos últimos 6 meses"
              />
            </div>
            <div className="mt-1.5 flex gap-1 text-[9px] text-tx-3">
              {data.vendas.vendas6M.map((m) => (
                <div key={m.anoMes} className="flex-1 text-center" title={`${nomeMesCurto(m.anoMes)}: ${money(m.valor)}`}>
                  {nomeMesCurto(m.anoMes)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Slider de ajuste + atalhos */}
      <div className="mt-3 flex items-center gap-3">
        <label className="text-[10px] uppercase tracking-wide text-tx-3">Ajuste</label>
        <input
          type="range"
          min={-100}
          max={100}
          step={1}
          value={ajustePct}
          onChange={(e) => setAjustePct(Number.parseInt(e.target.value, 10))}
          className="flex-1 accent-accent"
          aria-label="Percentual de ajuste"
        />
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={-100}
            max={100}
            step={1}
            value={ajustePct}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value, 10);
              if (Number.isNaN(v)) setAjustePct(0);
              else setAjustePct(Math.max(-100, Math.min(100, v)));
            }}
            className="h-7 w-14 rounded-iris-sm border border-line-strong bg-surface-2 px-1 text-right text-xs text-tx focus:outline-none focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent-soft"
          />
          <span className="text-xs text-tx-3">%</span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {[-10, -5, 0, +5, +10, +20].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setAjustePct(p)}
            className={`rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
              ajustePct === p
                ? 'bg-accent text-white'
                : 'border border-line bg-surface text-accent-text hover:bg-accent-soft'
            }`}
          >
            {p > 0 ? `+${p}%` : `${p}%`}
          </button>
        ))}
        {/* Sugestão alinhada ao crescimento real (quando há vendas com tendência) */}
        {data.vendas && !data.vendas.semHistorico && Math.abs(cresc) >= 1 && (
          <button
            type="button"
            onClick={() => setAjustePct(Math.round(cresc))}
            title={`Acompanhar tendência de vendas (${cresc > 0 ? '+' : ''}${cresc.toFixed(1)}%)`}
            className={`rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
              ajustePct === Math.round(cresc)
                ? 'bg-ok text-white'
                : `border border-ok-border bg-surface ${crescColor} hover:bg-ok-soft`
            }`}
          >
            📈 Seguir vendas ({cresc > 0 ? '+' : ''}{Math.round(cresc)}%)
          </button>
        )}
      </div>

      {/* Meta final + aplicar */}
      <div className="mt-3 flex items-center justify-between gap-2 rounded-iris border border-line-strong bg-surface px-3 py-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-tx-3">Meta sugerida</div>
          <div className="text-lg font-bold text-tx">{money(metaFinal)}</div>
        </div>
        <button
          type="button"
          onClick={() => onAplicar(metaFinal)}
          className={btnPrimario}
        >
          Aplicar <ArrowRightIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default SugestaoMetaInteligente;
